import { useEffect, useState } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function Reports() {
  const [days, setDays] = useState(30);
  const [cashier, setCashier] = useState([]);
  const [salesman, setSalesman] = useState([]);
  const [paymentMode, setPaymentMode] = useState([]);
  const [topCust, setTopCust] = useState([]);
  const [delivery, setDelivery] = useState({ summary: {}, items: [], total: 0 });

  const loadAll = async () => {
    try {
      const [c, sm, pm, tc, wd] = await Promise.all([
        api.get(`/reports/cashier-perf?days=${days}`),
        api.get(`/reports/salesman-perf?days=${days}`),
        api.get(`/reports/payment-mode?days=${days}`),
        api.get(`/reports/top-customers?days=${days}`),
        api.get(`/reports/whatsapp-delivery?days=${days}`),
      ]);
      setCashier(c.data); setSalesman(sm.data); setPaymentMode(pm.data); setTopCust(tc.data); setDelivery(wd.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Window</span>
        {[7, 30, 90, 365].map((d) => (
          <Button key={d} variant={days === d ? "default" : "outline"} size="sm" className="rounded-none" onClick={() => setDays(d)} data-testid={`reports-days-${d}`}>{d}d</Button>
        ))}
      </div>

      <Tabs defaultValue="salesman" data-testid="reports-tabs">
        <TabsList className="rounded-none">
          <TabsTrigger value="salesman" data-testid="tab-salesman">Salesman Performance</TabsTrigger>
          <TabsTrigger value="cashier" data-testid="tab-cashier">Cashier Performance</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment">Payment Mode</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Top Customers</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp Delivery</TabsTrigger>
        </TabsList>

        <TabsContent value="salesman" className="border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="salesman-perf-table">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="px-4 py-3 text-left font-semibold">Salesman</th>
              <th className="px-4 py-3 text-right font-semibold">Bills</th>
              <th className="px-4 py-3 text-right font-semibold">Revenue</th>
              <th className="px-4 py-3 text-right font-semibold">Avg Bill</th>
              <th className="px-4 py-3 text-right font-semibold">Items Sold</th>
              <th className="px-4 py-3 text-left font-semibold">Top Product</th>
              <th className="px-4 py-3 text-right font-semibold">You Saved (Given)</th>
              <th className="px-4 py-3 text-left font-semibold">Last Sale</th>
            </tr></thead>
            <tbody>
              {salesman.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No sales data for this window.</td></tr>}
              {salesman.map((r) => (
                <tr key={r.salesman_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`salesman-row-${r.salesman_id}`}>
                  <td className="px-4 py-3 font-medium">{r.salesman_name}{r.salesman_id === "unassigned" && <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400">No tag</span>}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.bills}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatINR(r.revenue)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatINR(r.avg_bill)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.total_items}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{r.top_product_name ? `${r.top_product_name} (${r.top_product_qty})` : "-"}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-green-700">{formatINR(r.total_savings_given || 0)}</td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-600">{(r.last_sale_at || "").slice(0, 16).replace("T", " ") || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabsContent>

        <TabsContent value="cashier" className="border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="cashier-perf-table">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="px-4 py-3 text-left font-semibold">Cashier</th>
              <th className="px-4 py-3 text-right font-semibold">Bills</th>
              <th className="px-4 py-3 text-right font-semibold">Revenue</th>
              <th className="px-4 py-3 text-right font-semibold">Avg Bill</th>
              <th className="px-4 py-3 text-right font-semibold">Discount Given</th>
            </tr></thead>
            <tbody>
              {cashier.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">No data.</td></tr>}
              {cashier.map((r) => (
                <tr key={r.cashier_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{r.cashier_name}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.bills}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatINR(r.revenue)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatINR(r.avg_bill)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">{formatINR(r.discount_given)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabsContent>

        <TabsContent value="payment" className="border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="payment-mode-table">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="px-4 py-3 text-left font-semibold">Mode</th>
              <th className="px-4 py-3 text-right font-semibold">Bills</th>
              <th className="px-4 py-3 text-right font-semibold">Revenue</th>
            </tr></thead>
            <tbody>
              {paymentMode.length === 0 && <tr><td colSpan={3} className="px-4 py-12 text-center text-slate-500">No data.</td></tr>}
              {paymentMode.map((r) => (
                <tr key={r.mode} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{r.mode}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.bills}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatINR(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabsContent>

        <TabsContent value="customers" className="border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="top-customers-table">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="px-4 py-3 text-left font-semibold">Customer</th>
              <th className="px-4 py-3 text-left font-semibold">Tier</th>
              <th className="px-4 py-3 text-right font-semibold">Bills</th>
              <th className="px-4 py-3 text-right font-semibold">Revenue</th>
            </tr></thead>
            <tbody>
              {topCust.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-500">No data.</td></tr>}
              {topCust.map((r) => (
                <tr key={r.customer_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{r.customer_name}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{r.tier || "-"}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.bills}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatINR(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabsContent>

        <TabsContent value="whatsapp" className="space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card label="Total messages" value={delivery.total || 0} />
            {Object.entries(delivery.summary || {}).map(([k, v]) => (
              <Card key={k} label={k} value={v} />
            ))}
          </div>
          <div className="border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="whatsapp-delivery-table">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className="px-4 py-3 text-left font-semibold">Sent At</th>
                <th className="px-4 py-3 text-left font-semibold">Phone</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Kind</th>
              </tr></thead>
              <tbody>
                {(delivery.items || []).length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-500">No deliveries.</td></tr>}
                {(delivery.items || []).map((n) => (
                  <tr key={n.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 text-slate-600 text-xs font-mono">{(n.sent_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-4 py-3 font-mono text-xs">{n.customer_phone}</td>
                    <td className="px-4 py-3 capitalize">{n.status}</td>
                    <td className="px-4 py-3 capitalize text-slate-600">{n.kind || (n.bill_id ? "bill" : "campaign")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="border border-slate-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>{value}</div>
    </div>
  );
}
